import BnumElementInternal from '../../bnum-element-states';
import { EMPTY_STRING, Nullable } from '@rotomeca/utils';
import style from './bnum-input.less';
import { Attr, Define, Self } from '../../../core/decorators';
import { TAG_INPUT } from '../../../core/utils/constants/tags';
import { Result, Risky } from '@rotomeca/rop';
import { ButtonVariation } from '../button';
import { Render } from './bnum-input.internal/template';
import {
	ATTRIBUTE_AUTOCOMPLETE,
	ATTRIBUTE_BUTTON,
	ATTRIBUTE_BUTTON_ICON,
	ATTRIBUTE_DATA_VALUE,
	ATTRIBUTE_DISABLED,
	ATTRIBUTE_ICON,
	ATTRIBUTE_IGNOREVALUE,
	ATTRIBUTE_INPUTMODE,
	ATTRIBUTE_MAXLENGTH,
	ATTRIBUTE_MINLENGTH,
	ATTRIBUTE_PATTERN,
	ATTRIBUTE_PLACEHOLDER,
	ATTRIBUTE_READONLY,
	ATTRIBUTE_REQUIRED,
	ATTRIBUTE_SPELLCHECK,
	ATTRIBUTE_STATE,
	ATTRIBUTE_TYPE,
	DEFAULT_BUTTON_VARIATION,
	DEFAULT_INPUT_TYPE,
	EVENT_CHANGE,
	EVENT_INPUT,
	ICON_ERROR,
	ICON_SUCCESS,
	ID_ERROR_TEXT,
	ID_INPUT,
	ID_INPUT_BUTTON,
	ID_INPUT_ICON,
	ID_STATE_ICON,
	ID_SUCCESS_TEXT,
	SLOT_BUTTON,
	SLOT_ERROR,
	SLOT_HINT,
	SLOT_SUCCESS,
	STATE_BUTTON,
	STATE_ERROR,
	STATE_OBI,
	STATE_STATE,
	STATE_SUCCESS,
	TEXT_ERROR_FIELD,
	TEXT_INVALID_INPUT,
	TEXT_VALID_INPUT,
} from './bnum-input.internal/constants';
import { STATE_DISABLED, STATE_ICON } from '../button/internal';
import { UI } from 'core/decorators/ui';
import {
	BnumInputCreateOptions,
	BnumInputUI,
	JsEventClick,
} from './bnum-input.internal/types';
import { Listener } from 'core/decorators/listener';
import { OnButtonClickedInitializer } from './bnum-input.internal/listeners';
import { Observe } from 'core/decorators/observe';
import base from './extra/bnum-input-base.less';
import styleStates from './extra/bnum-input-states.less';

export const INPUT_BASE_STYLE =
	BnumElementInternal.ConstructCSSStyleSheet(base);
export const INPUT_STYLE_STATES =
	BnumElementInternal.ConstructCSSStyleSheet(styleStates);

const OBSERVED_ATTRIBUTES = [
	ATTRIBUTE_DATA_VALUE,
	ATTRIBUTE_PLACEHOLDER,
	ATTRIBUTE_TYPE,
	ATTRIBUTE_DISABLED,
	ATTRIBUTE_STATE,
	ATTRIBUTE_BUTTON,
	ATTRIBUTE_BUTTON_ICON,
	ATTRIBUTE_ICON,
	ATTRIBUTE_REQUIRED,
	ATTRIBUTE_READONLY,
	ATTRIBUTE_PATTERN,
	ATTRIBUTE_MINLENGTH,
	ATTRIBUTE_MAXLENGTH,
	ATTRIBUTE_AUTOCOMPLETE,
	ATTRIBUTE_INPUTMODE,
	ATTRIBUTE_SPELLCHECK,
	'min',
	'max',
	'step',
] as const;

/**
 * Composant Input du design system Bnum.
 * Permet de gérer un champ de saisie enrichi avec gestion d'états, d'icônes, de bouton et d'accessibilité.
 *
 * @category Input
 *
 * @structure Sans rien
 * <bnum-input></bnum-input>
 *
 * @structure Avec une légende
 * <bnum-input>Label du champ</bnum-input>
 *
 * @structure Avec une légende et un indice
 * <bnum-input>
 * Label du champ
 * <span slot="hint">Indice d'utilisation</span>
 * </bnum-input>
 *
 * @structure Avec un bouton
 * <bnum-input button="true" button-icon="add">Label du champ
 *   <span slot="button">Ajouter</span>
 * </bnum-input>
 *
 * @structure En erreur
 * <bnum-input pattern="^[a-zA-Z0-9]+$" data-value="@@@@@">Label du champ
 * </bnum-input>
 *
 * @structure Avec un état de succès
 * <bnum-input state="success">Label du champ
 *   <span slot="success">Le champ est valide !</span>
 * </bnum-input>
 *
 * @structure Avec une icône
 * <bnum-input icon="search">Label du champ</bnum-input>
 *
 * @structure Avec un bouton avec icône seulement
 * <bnum-input placeholder="LA LA !" button-icon="add">Label du champ
 * </bnum-input>
 *
 * @structure Nombre
 * <bnum-input type="number" data-value="42">Label du champ</bnum-input>
 *
 * @structure Désactivé
 * <bnum-input disabled>
 *   Label du champ
 * </bnum-input>
 *
 * @structure Complet
 * <bnum-input
 *   data-value="Valeur initiale"
 *   placeholder="Texte indicatif"
 *   type="text"
 *   state="error"
 *   icon="search"
 *   button="primary"
 *   button-icon="send"
 * >
 *   Label du champ
 *   <span slot="hint">Indice d'utilisation</span>
 *   <span slot="success">Le champ est valide !</span>
 *   <span slot="error">Le champ est invalide !</span>
 *   <span slot="button">Envoyer</span>
 * </bnum-input>
 *
 * @slot (defaut) - Contenu du label principal du champ.
 * @slot hint - Contenu de l'indice d'utilisation (hint) du champ.
 * @slot success - Contenu du message de succès du champ.
 * @slot error - Contenu du message d'erreur du champ.
 * @slot button - Contenu du bouton interne (si présent).
 *
 * @state success - État de succès.
 * @state error - État d'erreur.
 * @state disabled - État désactivé.
 * @state icon - Présence d'une icône.
 * @state button - Présence d'un bouton.
 * @state obi - Bouton avec icône seulement (sans texte).
 * @state state - Présence d'un état (success / error).
 *
 * @event {MouseEvent} bnum-input:button.click - Événement déclenché au clic sur le bouton interne.
 * @event {InputEvent} input - Événement déclenché à la saisie dans le champ.
 * @event {Event} change - Événement déclenché au changement de valeur du champ.
 *
 * @attr {string} (optional) (default: undefined) data-value - Valeur initiale du champ.
 * @attr {string} (optional) (default: undefined) placeholder - Texte indicatif du champ.
 * @attr {string} (optional) (default: 'text') type - Type de l'input (text, password, email, etc.)
 * @attr {string} (optional) (default: undefined) disabled - Désactive le champ.
 * @attr {string} (optional) (default: undefined) state - État du champ (success, error, etc.).
 * @attr {string} (optional) (default: undefined) button - Présence d'un bouton interne (primary, secondary, danger, ...).
 * @attr {string} (optional) (default: undefined) button-icon - Icône du bouton interne.
 * @attr {string} (optional) (default: undefined) icon - Icône à afficher dans le champ.
 * @attr {string} (optional) (default: undefined) required - Champ requis.
 * @attr {string} (optional) (default: undefined) readonly - Champ en lecture seule.
 * @attr {string} (optional) (default: undefined) pattern - Expression régulière de validation.
 * @attr {string} (optional) (default: undefined) minlength - Longueur minimale du champ.
 * @attr {string} (optional) (default: undefined) maxlength - Longueur maximale du champ.
 * @attr {string} (optional) (default: undefined) autocomplete - Attribut autocomplete HTML.
 * @attr {string} (optional) (default: undefined) inputmode - Mode de saisie (mobile).
 * @attr {string} (optional) (default: undefined) spellcheck - Correction orthographique.
 * @attr {string} (optional) (default: undefined) ignorevalue - Attribut interne pour ignorer la synchronisation de valeur. Ne pas utiliser.
 * @attr {string} (optional) (default: undefined) name - Nom du champ (attribut HTML name).
 *
 * @cssvar {#666} --bnum-input-hint-text-color - Couleur du texte du hint.
 * @cssvar {#eee} --bnum-input-background-color - Couleur de fond de l'input.
 * @cssvar {#666} --bnum-input-color - Couleur du texte de l'input.
 * @cssvar {#3a3a3a} --bnum-input-line-color - Couleur de la ligne/bordure de l'input.
 * @cssvar {#36b37e} --bnum-input-state-success-color - Couleur de l'état de succès.
 * @cssvar {#de350b} --bnum-input-state-error-color - Couleur de l'état d'erreur.
 * @cssvar {inset 0 -2px 0 0 #3a3a3a} --bnum-input-box-shadow - Ombre portée de l'input.
 *
 */
@Define({
	tag: TAG_INPUT,
	// eslint-disable-next-line no-restricted-syntax
	template: Render(),
	styles: [INPUT_BASE_STYLE, INPUT_STYLE_STATES, style],
})
@Observe(OBSERVED_ATTRIBUTES)
export class HTMLBnumInput extends BnumElementInternal {
	//#region Constants
	static readonly SYNCED_ATTRIBUTES = [
		ATTRIBUTE_PATTERN,
		ATTRIBUTE_MINLENGTH,
		ATTRIBUTE_MAXLENGTH,
		ATTRIBUTE_AUTOCOMPLETE,
		ATTRIBUTE_INPUTMODE,
		ATTRIBUTE_SPELLCHECK,
		'min',
		'max',
		'step',
	] as const;
	//#endregion Constants

	//#region Private fields
	/**
	 * Valeur initiale (pour la réinitialisation du formulaire)
	 */
	#_initValue: string = EMPTY_STRING;
	//#endregion Private fields

	//#region Getters/Setters
	/** Référence à la classe HTMLBnumInput */
	@Self _!: typeof HTMLBnumInput;

	@UI({
		stateIcon: `#${ID_STATE_ICON}`,
		icon: `#${ID_INPUT_ICON}`,
		button: `#${ID_INPUT_BUTTON}`,
		input: `#${ID_INPUT}`,
	})
	accessor #_ui!: BnumInputUI;

	/**
	 * Permet d'écouter le clic sur le bouton interne.
	 */
	@Listener(OnButtonClickedInitializer, { lazy: false })
	accessor onButtonClicked!: JsEventClick;
	// -- Formulaire --

	/**
	 * Nom du champ (attribut HTML name).
	 */
	@Attr() accessor name: string = EMPTY_STRING;

	/**
	 * Valeur courante du champ de saisie.
	 */
	get value(): string {
		return (
			this.#_ui.input?.value ||
			this.getAttribute(ATTRIBUTE_DATA_VALUE) ||
			EMPTY_STRING
		);
	}
	set value(val: string) {
		if (this.#_ui.input === null) this.setAttribute(ATTRIBUTE_DATA_VALUE, val);
		else {
			this.#_ui.input!.value = val;
			this.#_setFormValue(val);
		}
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	/**
	 * Constructeur du composant.
	 * Initialise la valeur initiale à partir de l'attribut data-value.
	 */
	constructor() {
		super();

		this.#_initValue = this.getAttribute(ATTRIBUTE_DATA_VALUE) ?? EMPTY_STRING;
	}

	/**
	 * Attache un Shadow DOM personnalisé.
	 */
	protected _p_attachCustomShadow(): Nullable<ShadowRoot> {
		return this.attachShadow({ mode: 'open', delegatesFocus: true });
	}

	/**
	 * Construit le DOM interne et attache les écouteurs d'événements.
	 */
	protected _p_buildDOM(): void {
		this.#_ui.input!.addEventListener(EVENT_INPUT, (e: Event) => {
			this.#_inputValueChangedCallback(e);
		});

		this.#_ui.input!.addEventListener(EVENT_CHANGE, (e: Event) => {
			this.#_inputValueChangedCallback(e);
		});

		this.#_initialiseButton().#_update().#_removeValueAttribute();
	}

	/**
	 * Met à jour le composant lors d'un changement d'attribut.
	 */
	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		if (this.alreadyLoaded === false) return 'break';

		if (newVal == oldVal) return;

		switch (name) {
			case ATTRIBUTE_DATA_VALUE:
				if (this.attr(ATTRIBUTE_IGNOREVALUE) !== null) {
					this.removeAttribute(ATTRIBUTE_IGNOREVALUE);
					break;
				}
				if (newVal !== null) {
					this._p_internal.setFormValue(newVal);

					if (this.#_ui.input) this.#_ui.input.value = newVal;

					this.#_removeValueAttribute();
				}
				break;

			default:
				break;
		}
	}

	/**
	 * Appelé après le flush du DOM pour synchroniser l'état.
	 */
	protected _p_postFlush(): void {
		this.#_update();
	}
	//#endregion Lifecycle

	//#region Public methods
	// --- Formulaire --
	/**
	 * Réinitialise la valeur du champ lors d'une remise à zéro du formulaire parent.
	 */
	formResetCallback(): void {
		this.value = this.#_initValue;
	}

	/**
	 * Active ou désactive le champ selon l'état du fieldset parent.
	 */
	formDisabledCallback(disabled: boolean): void {
		if (disabled) this.setAttribute(ATTRIBUTE_DISABLED, 'disabled');
		this.#_sync();
	}

	// -- Helper --
	/**
	 * Active le bouton interne avec texte, icône et variation éventuels.
	 * @param options Objet contenant le texte, l'icône et la variation du bouton.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	enableButton({
		text = undefined,
		icon = undefined,
		variation = DEFAULT_BUTTON_VARIATION,
	}: { text?: string; icon?: string; variation?: ButtonVariation } = {}): this {
		this.setAttribute(ATTRIBUTE_BUTTON, variation);

		if (text !== undefined) {
			this.querySelector(`slot[name="${SLOT_BUTTON}"]`)?.remove?.();
			const span = this._p_createSpan({
				child: text,
				attributes: { slot: 'button' },
			});
			this.appendChild(span);
		}

		if (icon !== undefined) {
			this.setAttribute(ATTRIBUTE_BUTTON_ICON, icon);
		}
		return this;
	}

	/**
	 * Active uniquement l'icône du bouton interne (sans texte).
	 * @param icon Nom de l'icône à afficher sur le bouton.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	enableButtonIconOnly(icon: string): this {
		this.querySelector(`slot[name="${SLOT_BUTTON}"]`)?.remove?.();
		this.removeAttribute(ATTRIBUTE_BUTTON);
		this.setAttribute(ATTRIBUTE_BUTTON_ICON, icon);
		return this;
	}

	/**
	 * Masque le bouton interne.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	hideButton(): this {
		this.removeAttribute(ATTRIBUTE_BUTTON);
		this.removeAttribute(ATTRIBUTE_BUTTON_ICON);
		return this;
	}

	/**
	 * Définit l'état de succès avec un message optionnel.
	 * @param message Message de succès à afficher.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	setSuccessState(message?: string): this {
		return this.#_setState(SLOT_SUCCESS, message);
	}

	/**
	 * Définit l'état d'erreur avec un message optionnel.
	 * @param message Message d'erreur à afficher.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	setErrorState(message?: string): this {
		return this.#_setState(SLOT_ERROR, message);
	}

	/**
	 * Définit une icône à afficher dans le champ.
	 * @param icon Nom de l'icône à afficher.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	setIcon(icon: string): this {
		this.setAttribute(ATTRIBUTE_ICON, icon);
		return this;
	}

	/**
	 * Supprime l'icône affichée dans le champ.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	removeIcon(): this {
		this.removeAttribute(ATTRIBUTE_ICON);
		return this;
	}

	/**
	 * Définit un indice d'utilisation (hint) pour le champ.
	 * @param hint Texte de l'indice à afficher.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	setHint(hint: string): this {
		this.removeHint();
		const span = this._p_createSpan({
			child: hint,
			attributes: { slot: SLOT_HINT },
		});
		this.appendChild(span);
		return this;
	}

	/**
	 * Supprime l'indice d'utilisation (hint) du champ.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	removeHint(): this {
		this.querySelector(`slot[name="${SLOT_HINT}"]`)?.remove?.();
		return this;
	}

	/**
	 * Définit le label principal du champ.
	 * @param label Texte ou élément HTML à utiliser comme label.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	setLabel(label: string | HTMLElement): this {
		// On supprime tout ce qui n'a pas l'attribut slot
		const nodes = Array.from(this.childNodes);
		for (const node of nodes) {
			if (node instanceof HTMLElement) {
				const element = node as HTMLElement;

				if (!element.hasAttribute('slot')) this.removeChild(element);
			}
		}

		if (typeof label === 'string')
			this.appendChild(this._p_createTextNode(label));
		else this.appendChild(label);

		return this;
	}
	//#endregion Public methods

	//#region Private methods
	/**
	 * Supprime `data-value` et ajoute `ignorevalue` avant pour éviter les effets de bords
	 * @returns Chaînage
	 */
	#_removeValueAttribute(): this {
		this.attr(ATTRIBUTE_IGNOREVALUE, 'true').removeAttribute(
			ATTRIBUTE_DATA_VALUE,
		);
		return this;
	}

	/**
	 * Met à jour l'état visuel et fonctionnel du composant selon ses attributs.
	 * @private
	 * @returns {this} L'instance courante pour chaînage.
	 */
	#_update(): this {
		this._p_clearStates();

		if (this.#_ui.input?.value || false) this._p_addState('value');

		const btnValue = this.attr(ATTRIBUTE_BUTTON);
		if (btnValue !== null) {
			this._p_addState(STATE_BUTTON);
			switch (btnValue) {
				case ButtonVariation.PRIMARY:
					this.#_ui.button!.variation = ButtonVariation.PRIMARY;
					break;

				case ButtonVariation.SECONDARY:
					this.#_ui.button!.variation = ButtonVariation.SECONDARY;
					break;

				case ButtonVariation.DANGER:
					this.#_ui.button!.variation = ButtonVariation.DANGER;
					break;

				default:
					break;
			}
		}

		const button_icon = this.attr(ATTRIBUTE_BUTTON_ICON);
		if (button_icon !== null) {
			this.#_ui.button!.icon = button_icon;

			if (!this._p_hasState(STATE_BUTTON))
				this._p_addStates(STATE_BUTTON, STATE_OBI);
			else if (btnValue === EMPTY_STRING) this._p_addState(STATE_OBI);
		}

		const icon = this.attr(ATTRIBUTE_ICON);
		if (icon !== null) {
			this._p_addState(STATE_ICON);
			this.#_ui.icon!.icon = icon;
		}

		if (this.attr(ATTRIBUTE_DISABLED) !== null)
			this._p_addState(STATE_DISABLED);

		return this.#_updateState(this.attr(ATTRIBUTE_STATE)).#_sync();
	}

	/**
	 * Synchronise les propriétés et attributs de l'input interne.
	 * Met à jour les propriétés HTML de l'input selon les attributs du composant.
	 * @private
	 * @returns {this} L'instance courante pour chaînage.
	 */
	#_sync(): this {
		if (!this.#_ui.input) return this;

		const input = this.#_ui.input;

		// 1. Propriétés de base
		input.value = this.value;
		input.type = this.getAttribute(ATTRIBUTE_TYPE) || DEFAULT_INPUT_TYPE;
		input.placeholder =
			this.getAttribute(ATTRIBUTE_PLACEHOLDER) || EMPTY_STRING;

		// 2. États Booléens (On utilise .disabled / .readOnly pour la réactivité JS)
		input.disabled =
			this.hasAttribute(ATTRIBUTE_DISABLED) || this._p_hasState(STATE_DISABLED);
		input.readOnly = this.hasAttribute(ATTRIBUTE_READONLY);
		input.required = this.hasAttribute(ATTRIBUTE_REQUIRED);

		// 3. Validation & UX (On utilise setAttribute pour les attributs HTML5)
		for (const attr of this._.SYNCED_ATTRIBUTES) {
			this.#_setFieldAttr(attr);
		}

		return this.#_updateA11y();
	}

	/**
	 * Met à jour l'accessibilité (a11y) de l'input selon l'état.
	 * Met à jour les attributs ARIA et la validité de l'input.
	 * @private
	 * @returns {this} L'instance courante pour chaînage.
	 */
	#_updateA11y(): this {
		if (!this.#_ui.input) return this;

		return this.#_setValidity();
	}

	/**
	 * Met à jour l'état visuel selon l'état passé en paramètre.
	 * @private
	 * @param state L'état à appliquer (success, error, etc.)
	 * @returns {this} L'instance courante pour chaînage.
	 */
	#_updateState(state: Nullable<string>): this {
		if (state !== null) {
			switch (state) {
				case STATE_SUCCESS:
					this._p_addStates(STATE_STATE, STATE_SUCCESS);
					this.#_ui.stateIcon!.icon = ICON_SUCCESS;
					break;

				case STATE_ERROR:
					this._p_addStates(STATE_STATE, STATE_ERROR);
					this.#_ui.stateIcon!.icon = ICON_ERROR;
					break;

				default:
					break;
			}
		}

		return this;
	}

	/**
	 * Définit l'état (succès ou erreur) et le message associé.
	 * @private
	 * @param state Type d'état (success ou error).
	 * @param message Message à afficher.
	 * @returns {this} L'instance courante pour chaînage.
	 */
	#_setState(
		state: typeof SLOT_SUCCESS | typeof SLOT_ERROR,
		message?: string,
	): this {
		this.setAttribute(ATTRIBUTE_STATE, state);

		if (message) {
			this.querySelector(`slot[name="${state}"]`)?.remove?.();
			const span = this._p_createSpan({
				child: message,
				attributes: { slot: state },
			});
			this.appendChild(span);
		}

		return this;
	}

	/**
	 * Met à jour la validité de l'input et les messages d'erreur/succès.
	 * Gère également les attributs ARIA liés à la validation.
	 * @private
	 * @returns {this} L'instance courante pour chaînage.
	 */
	#_setValidity(): this {
		if (!this.#_ui.input) return this;

		const stateAttr = this.attr(ATTRIBUTE_STATE);
		const isManualError = stateAttr === STATE_ERROR;

		if (isManualError) {
			this.#_internalSetValidity(
				{ customError: true },
				TEXT_ERROR_FIELD,
				this.#_ui.input,
			);
		} else {
			this.#_safeCheckValidity().match({
				Ok: isValid => {
					const isSuccess =
						isValid && this.#_ui.input!.validationMessage === EMPTY_STRING;

					if (isSuccess) {
						this.#_internalSetValidity({});
					} else {
						this.#_internalSetValidity(
							this.#_ui.input!.validity,
							this.#_ui.input!.validationMessage,
							this.#_ui.input!,
						);
					}

					return void 0;
				},
				Err: () => this.#_internalSetValidity({}), // Fallback de sécurité
			});
		}

		return this.#_syncValidationUI(isManualError);
	}

	/**
	 * Gère l'interface utilisateur de validation (messages, icônes, ARIA).
	 * @param isManualError Si l'erreur est définie manuellement via l'attribut state.
	 * @returns Cette instance pour chaînage.
	 */
	#_syncValidationUI(isManualError: boolean): this {
		const input = this.#_ui.input!;
		const hasNativeError = input.validationMessage !== EMPTY_STRING;
		const isError = isManualError || (hasNativeError && !input.validity.valid);
		const isSuccess = !isManualError && hasNativeError && input.validity.valid;
		const hasState = isError || isSuccess;

		if (hasState) {
			this._p_addStates(STATE_STATE, isSuccess ? STATE_SUCCESS : STATE_ERROR);

			const successText =
				this.#_ui.input!.validationMessage || TEXT_VALID_INPUT;
			const errorText =
				this.#_ui.input!.validationMessage || TEXT_INVALID_INPUT;
			const validationText = isSuccess ? successText : errorText;
			const slotTextId = isSuccess ? ID_SUCCESS_TEXT : ID_ERROR_TEXT;

			this.shadowRoot!.querySelector<HTMLSlotElement>(
				`#${slotTextId} slot`,
			)!.innerText = validationText;

			input.setAttribute('aria-invalid', isError ? 'true' : 'false');

			const descriptions: string[] = [];
			if (isError) descriptions.push(ID_ERROR_TEXT);
			if (isSuccess) descriptions.push(ID_SUCCESS_TEXT);

			input.setAttribute('aria-describedby', descriptions.join(' '));
		} else {
			input.removeAttribute('aria-invalid');
			input.removeAttribute('aria-describedby');
		}

		const finalState = isError ? STATE_ERROR : isSuccess ? STATE_SUCCESS : null;
		return this.#_updateState(finalState);
	}

	/**
	 * Initialise le bouton interne et son écouteur de clic.
	 * Ajoute un écouteur d'événement sur le bouton si nécessaire.
	 * @private
	 * @returns {this} L'instance courante pour chaînage.
	 */
	#_initialiseButton(): this {
		if (this.#_ui.button !== null) {
			this.#_ui.button.addEventListener('click', (e:Event) => {
				this.onButtonClicked.call(e);
			});
		}

		return this;
	}

	/**
	 * Callback appelé lors d'un changement de valeur de l'input.
	 * @private
	 * @param e Evénement de changement de valeur.
	 */
	#_inputValueChangedCallback(e: Event): void {
		this._p_inputValueChangedCallback(e);
	}

	/**
	 * Callback protégé appelé lors d'un changement de valeur de l'input.
	 * @protected
	 * @param e Evénement de changement de valeur.
	 * @returns Résultat de l'opération.
	 */
	@Risky()
	protected _p_inputValueChangedCallback(e: Event): Result<void> {
		this.#_setFormValue(this.#_ui.input!.value);
		this.#_update();

		return this.#_dispatchEvent(e).tapError(() => {
			this.#_dispatchInputEventFallback(e);
		});
	}

	/**
	 * Transfère un attribut du composant vers l'input interne si présent.
	 * @private
	 * @param attrName Nom de l'attribut à synchroniser.
	 */
	#_setFieldAttr(attrName: string): this {
		const val = this.getAttribute(attrName);
		if (val !== null) this.#_ui.input!.setAttribute(attrName, val);
		else this.#_ui.input!.removeAttribute(attrName);

		return this;
	}

	/**
	 * Définit la valeur du formulaire interne.
	 * @param value Valeur à définir.
	 * @returns Résultat de l'opération.
	 */
	@Risky()
	#_setFormValue(value: string): Result<void> {
		this._p_internal.setFormValue(value);
		return Result.Ok();
	}

	/**
	 * Met à jour la validité interne de l'input.
	 * @param flags Drapeaux de validité.
	 * @param message Message de validation.
	 * @param anchor Ancre HTML pour le message.
	 * @returns Résultat de l'opération.
	 */
	@Risky()
	#_internalSetValidity(
		flags?: ValidityStateFlags | undefined,
		message?: string | undefined,
		anchor?: HTMLElement | undefined,
	): Result<void> {
		return this._p_internal.setValidity(flags, message, anchor) as any;
	}

	/**
	 * Fait une vérification sécurisée de la validité de l'input.
	 * @returns Résultat de l'opération avec la validité.
	 */
	@Risky()
	#_safeCheckValidity(): Result<boolean> {
		return this.#_ui.input!.checkValidity() as any;
	}

	/**
	 * Effectue la dispatch de l'événement passé en paramètre.
	 * @param e Evénement à dispatcher.
	 * @returns Résultat de l'opération.
	 */
	@Risky()
	#_dispatchEvent(e: Event): Result<void> {
		this.dispatchEvent(e);
		return Result.Ok();
	}

	/**
	 * Fallback pour la dispatch des événements input/change.
	 * @param e Evènement qui pose problème
	 */
	#_dispatchInputEventFallback(e: Event): void {
		this.dispatchEvent(
			e.type === 'input'
				? new InputEvent('input', {
						data: this.value,
						inputType: this.attr('type') || 'text',
					})
				: new Event('change'),
		);
	}
	//#endregion Private methods

	//#region Protected methods
	protected _p_initialiseButton() {
		return this.#_initialiseButton();
	}
	//#endregion Protected methods

	//#region Static methods
	/**
	 * Crée une instance du composant avec les options fournies.
	 * @param label Texte du label principal.
	 * @param options Options d'initialisation (attributs et slots).
	 * @returns {HTMLBnumInput} Instance du composant.
	 */
	static Create(
		label: string,
		options: BnumInputCreateOptions = {},
	): HTMLBnumInput {
		const slotsMap: Partial<Record<keyof BnumInputCreateOptions, string>> = {
			hint: SLOT_HINT,
			success: SLOT_SUCCESS,
			error: SLOT_ERROR,
			btnText: SLOT_BUTTON,
		};
		const el = document.createElement(this.TAG) as HTMLBnumInput;
		el.textContent = label;

		for (const [key, value] of Object.entries(options)) {
			if (value === undefined) continue;

			const slotName = slotsMap[key as keyof BnumInputCreateOptions];
			if (slotName) this.#_CreateSlotElement(el, slotName, value);
			else el.setAttribute(key, value);
		}

		return el;
	}

	/**
	 * Helper privé statique pour créer les éléments de slot.
	 */
	static #_CreateSlotElement(
		node: HTMLBnumInput,
		slotName: string,
		content: Nullable<string>,
	) {
		if (content) {
			const element = document.createElement('span');
			element.slot = slotName;
			element.textContent = content;
			node.appendChild(element);
		}
	}

	static CreateRender(html: string = EMPTY_STRING): string {
		// eslint-disable-next-line no-restricted-syntax
		return Render(html);
	}
	//#endregion Static methods
}
